import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../store";
import { useLicenseGate } from "../../../hooks/useLicenseGate";
import { PiLockKeyLight, PiEnvelopeSimpleLight, PiCheckCircleLight } from "react-icons/pi";
import Button from "../TailwindComponents/Button";
import { requestFeatureFromAdmin } from "../../../store/actions/PlanActions";
import { useAppDispatch } from "../../../store";
import { UPGRADE_PATH } from "../../../config/entitlementKeys";

const LICENSING_ENABLED = process.env.REACT_APP_LICENSING_ENABLED === "true";

/**
 * NoSeatGate - shown instead of page content when a user has no base plan seat.
 *
 * Admin exception: admins can always navigate to /account (Plans page),
 * /admin, and /settings even without a seat - they need those to manage seats.
 */
const NoSeatGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { loaded, hasBaseSeat } = useLicenseGate();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isOrgAdmin = userInfo?.is_org_admin === true;

  const [requestSending, setRequestSending] = React.useState(false);
  const [requestSent, setRequestSent] = React.useState(false);

  // Don't gate if licensing is off or data hasn't loaded
  if (!LICENSING_ENABLED || !loaded) return <>{children}</>;

  // User has a seat - pass through
  if (hasBaseSeat) return <>{children}</>;

  // Admin exception: always allow admin pages
  if (isOrgAdmin) {
    const path = window.location.pathname;
    if (path.startsWith("/account") || path.startsWith("/admin") || path.startsWith("/settings")) {
      return <>{children}</>;
    }
  }

  const handleRequestSeat = async () => {
    if (requestSending) return;
    setRequestSending(true);
    const success = await dispatch(
      requestFeatureFromAdmin("base_plan_seat", t("I need a seat to access the platform."))
    );
    setRequestSending(false);
    if (success) setRequestSent(true);
  };

  return (
    <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900/50">
      <div className="glass flex flex-col items-center text-center px-8 py-10 max-w-md rounded-xl shadow-lg">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-warning-50 dark:bg-warning-900/30 mb-4">
          <PiLockKeyLight className="w-7 h-7 text-warning-600 dark:text-warning-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {t("No Active Seat")}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
          {t("You don't currently have a seat on your organization's plan.")}{" "}
          {isOrgAdmin
            ? t("Go to Plans & Pricing to manage seat assignments.")
            : t("Contact your administrator to get a seat assigned.")}
        </p>

        {isOrgAdmin ? (
          <Button
            variant="primary"
            size="small"
            onClick={() => history.push(UPGRADE_PATH)}
            className="w-full"
          >
            {t("Manage Seats")}
          </Button>
        ) : requestSent ? (
          <div className="flex items-center justify-center gap-2 text-sm text-success-600 dark:text-success-400 font-medium">
            <PiCheckCircleLight className="w-4 h-4" />
            {t("Request sent to your admin")}
          </div>
        ) : (
          <Button
            variant="primary"
            size="small"
            onClick={handleRequestSeat}
            disabled={requestSending}
            icon={<PiEnvelopeSimpleLight className="w-4 h-4" />}
            className="w-full"
          >
            {requestSending ? t("Sending...") : t("Request a Seat")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default NoSeatGate;
